import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { User, Mail, Dices, MessageSquare, Award, ArrowLeft, Calendar, Phone, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { membersAPI, tablesAPI, getUploadUrl } from '../services/api';
import { TableBadges } from '../components/TableBadges';
import { toast } from 'sonner';

const MemberProfile = () => {
  const { userId } = useParams();
  const { user: currentUser, isAdmin } = useAuth();
  const [member, setMember] = useState(null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if current user can see sensitive info (phone/address)
  // Admin, superadmin, or bureau member
  const canSeeSensitiveInfo = () => {
    if (!currentUser) return false;
    if (['admin', 'superadmin'].includes(currentUser.role)) return true;
    if (currentUser.bureau_role) return true;
    return false;
  };

  useEffect(() => {
    loadMember();
  }, [userId]);

  const loadMember = async () => {
    try {
      setLoading(true);
      const [members, tablesData] = await Promise.all([membersAPI.getAll(), tablesAPI.getAll()]);
      setTables(tablesData);
      const foundMember = members.find(m => m.user_id === userId);
      if (foundMember) {
        setMember(foundMember);
      } else {
        toast.error('Membre non trouvé');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getAvatarUrl = (avatar) => getUploadUrl(avatar);

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Administrateur', className: 'bg-purple-600 text-white' },
      member: { label: 'Membre', className: 'bg-green-600 text-white' },
      guest: { label: 'Invité', className: 'bg-gray-600 text-white' }
    };
    return badges[role] || badges.guest;
  };

  const getExperienceLabel = (level) => {
    const levels = {
      debutant: 'Débutant - Découvre le JDR',
      intermediaire: 'Intermédiaire - Quelques parties',
      expert: 'Expert - Vétéran des tables'
    };
    return levels[level] || 'Non spécifié';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8 text-center">
          <User className="h-16 w-16 text-purple-400/50 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-purple-300 mb-2">Membre non trouvé</h2>
          <p className="text-gray-400 mb-6">Ce profil n'existe pas ou a été supprimé.</p>
          <Link to="/dashboard">
            <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au dashboard
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const isOwnProfile = currentUser?.user_id === member.user_id;

  return (
    <div className="min-h-screen pt-24 px-4 pb-12">
      <div className="container mx-auto max-w-4xl">
        {/* Back button */}
        <Link to="/dashboard" className="inline-flex items-center text-purple-400 hover:text-purple-300 mb-6 transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour aux membres
        </Link>

        {/* Profile Header */}
        <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-8 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {member.avatar ? (
                <img 
                  src={getAvatarUrl(member.avatar)} 
                  alt={member.name}
                  className="w-32 h-32 rounded-full border-4 border-purple-500/50 object-cover drop-shadow-[0_0_20px_rgba(147,51,234,0.5)]"
                />
              ) : (
                <div className="w-32 h-32 rounded-full border-4 border-purple-500/50 bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-5xl font-bold text-white drop-shadow-[0_0_20px_rgba(147,51,234,0.5)]">
                  {member.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold text-purple-300 mb-2">{member.name}</h1>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                <Badge className={getRoleBadge(member.role).className}>
                  {getRoleBadge(member.role).label}
                </Badge>
                {member.join_date && (
                  <Badge variant="outline" className="border-purple-500/50 text-purple-300">
                    <Calendar className="mr-1 h-3 w-3" />
                    Membre depuis {new Date(member.join_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </Badge>
                )}
              </div>
              <TableBadges tableIds={member.tables} tables={tables} className="justify-center md:justify-start mb-4" />

              {/* Email - visible only to admins */}
              {isAdmin() && (
                <div className="flex items-center justify-center md:justify-start text-gray-400 mb-2">
                  <Mail className="mr-2 h-4 w-4 text-purple-400" />
                  {member.email}
                </div>
              )}

              {/* Discord */}
              {member.profile?.discord_username && (
                <div className="flex items-center justify-center md:justify-start text-gray-400 mb-2">
                  <MessageSquare className="mr-2 h-4 w-4 text-purple-400" />
                  Discord: <span className="text-purple-300 ml-1">{member.profile.discord_username}</span>
                </div>
              )}

              {/* Phone - visible only to admins and bureau members */}
              {canSeeSensitiveInfo() && member.profile?.phone && (
                <div className="flex items-center justify-center md:justify-start text-gray-400 mb-2">
                  <Phone className="mr-2 h-4 w-4 text-purple-400" />
                  <span className="text-purple-300">{member.profile.phone}</span>
                </div>
              )}

              {/* Address - visible only to admins and bureau members */}
              {canSeeSensitiveInfo() && member.profile?.address && (
                <div className="flex items-center justify-center md:justify-start text-gray-400 mb-2">
                  <MapPin className="mr-2 h-4 w-4 text-purple-400" />
                  <span className="text-purple-300">{member.profile.address}</span>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-3">
                {isOwnProfile ? (
                  <Link to="/profile">
                    <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400">
                      Modifier mon profil
                    </Button>
                  </Link>
                ) : (
                  <Link to="/messages">
                    <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Envoyer un message
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Profile Details */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Experience & Games */}
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-6">
            <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-center">
              <Dices className="mr-2 h-5 w-5" />
              Jeux de rôle
            </h3>
            
            {member.profile?.experience_level && (
              <div className="mb-4">
                <p className="text-gray-500 text-sm mb-1">Niveau d'expérience</p>
                <div className="flex items-center">
                  <Award className="mr-2 h-4 w-4 text-purple-400" />
                  <span className="text-purple-200">{getExperienceLabel(member.profile.experience_level)}</span>
                </div>
              </div>
            )}

            {member.profile?.favorite_games && member.profile.favorite_games.length > 0 ? (
              <div>
                <p className="text-gray-500 text-sm mb-2">Jeux préférés</p>
                <div className="flex flex-wrap gap-2">
                  {member.profile.favorite_games.map((game, index) => (
                    <Badge 
                      key={index} 
                      variant="outline" 
                      className="border-purple-500/50 text-purple-300 bg-purple-900/30"
                    >
                      {game}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 italic">Aucun jeu favori renseigné</p>
            )}
          </Card>

          {/* Bio */}
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-6">
            <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-center">
              <User className="mr-2 h-5 w-5" />
              À propos
            </h3>
            
            {member.profile?.bio ? (
              <p className="text-gray-300 whitespace-pre-line leading-relaxed">{member.profile.bio}</p>
            ) : (
              <p className="text-gray-500 italic">Aucune bio renseignée</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MemberProfile;
